/**
 * tutorLiveCenterViewModel — l'activité au centre du live du masteur, en props de `<TutorLiveCenter>`.
 *
 * Pur. Trois choses :
 *  - **les lectures du store** (`selectLiveCenterSlices`, un `createSelector` sur ses tranches) : les élèves
 *    de la séance (`live_sessions[].users`), ce que la salle dit de chacun (l'activité qu'il a devant lui,
 *    `currentStudentActivity` / `seenStudentActivity`, écrits par l'écouteur global `LKEvents`), qui est
 *    connecté, qui est avec le champion, la demande « Voir le rendu » du fil ;
 *  - **l'onglet** (`tutorLiveCenterTabReducer`) : le défaut d'une activité (`TodoModal.getDefaultMode`),
 *    la bascule sur le rendu quand une photo arrive, l'onglet gardé par son NOM quand le quiz est retiré
 *    (l'ancien le tenait par son index et pouvait viser le mauvais, C-60), « Contrôles » qui se referme
 *    quand on change d'élève (`TutorFocusLayoutM:83`) ;
 *  - **l'activité regardée** (`tutorLiveCenterFocus`) et **les gestes offerts** (`tutorLiveCenterActions`),
 *    les conditions de `TodoModal.renderHeaderActions` / `renderMenuItems` (branche tuteur, au centre).
 */
import { createSelector } from "reselect"

import { toQuizReview, type ApiQuizForReview } from "../../../../../engine/activity/prerequisite/quizReview"
import { requiresPrerequisiteQuiz } from "../../../../../engine/activity/prerequisite/prerequisiteGate"
import { type UICorrectionFeedback } from "../../../ui/types/studentActivity"
import { type UIQuizReview } from "../../../ui/types/studentQuizReview"
import { type UIActivityDurationTone } from "../../../ui/types/activity"
import { type UITutorActivityAction } from "../../../ui/types/tutorEleves"
import {
    type UITutorLiveAlert,
    type UITutorLiveCenterTab,
    type UITutorLiveFocus,
    type UITutorLiveStudent,
    type UITutorStatementAid,
    type UITutorWhiteboardVisual,
} from "../../../ui/types/tutorLive"
import {
    findLiveActivity,
    liveActivityCaption,
    liveActivityHasWhiteboard,
    liveActivityHasWork,
    liveActivityStatus,
    liveActivityTitle,
    sameId,
    toUITutorLiveStatement,
    toUITutorLiveWork,
    type ApiLiveActivity,
    type ApiLiveStudent,
} from "../../../adapters/tutor/Live/tutorLiveActivityAdapter"
import { selectAccount } from "../../shared/displayedStudent"
import { toUIQuizReviewQuestion } from "../../student/Activity/quizReviewViewModel"
import { durationToneOf } from "../../student/Activities/studentActivitiesViewModel"

// ── Les lectures du store ──────────────────────────────────────────────────────────────────────────

export type LiveCenterSlices = {
    liveSessionId: number | null
    /** Les élèves de la séance, sans le masteur. */
    students: ApiLiveStudent[]
    /** L'activité ouverte par chaque élève (`liveMode.currentStudentActivity`). */
    currentStudentActivity: Record<string, unknown>
    /** Ce que montre chaque appareil (`liveMode.seenStudentActivity`, par identité). */
    seenStudentActivity: Record<string, unknown>
    focusRequest: { studentId?: unknown; activityId?: unknown; requestId?: number } | null
    onlineIds: unknown[]
    checkpointIds: unknown[]
}

const EMPTY: never[] = []
const NO_MAP: Record<string, unknown> = {}

/* eslint-disable @typescript-eslint/no-explicit-any */
const selectLiveSessions = (state: any) => (selectAccount(state) as any)?.live_sessions as unknown[] | undefined
const selectMeId = (state: any) => (selectAccount(state) as any)?.id as number | undefined
const selectLiveSessionId = (state: any) => (state?.liveMode?.liveSessionId ?? null) as number | null
const selectCurrent = (state: any) => (state?.liveMode?.currentStudentActivity ?? NO_MAP) as Record<string, unknown>
const selectSeen = (state: any) => (state?.liveMode?.seenStudentActivity ?? NO_MAP) as Record<string, unknown>
const selectFocusRequest = (state: any) =>
    (state?.liveMode?.studentActivityFocusRequest ?? null) as LiveCenterSlices["focusRequest"]
const selectOnline = (state: any) => (state?.liveMode?.liveParticipants ?? EMPTY) as unknown[]
const selectCheckpoints = (state: any) => (state?.liveMode?.ongoingCheckpointIds ?? EMPTY) as unknown[]
/* eslint-enable @typescript-eslint/no-explicit-any */

const selectLiveStudents = createSelector(
    [selectLiveSessions, selectLiveSessionId, selectMeId],
    (sessions, liveSessionId, meId): ApiLiveStudent[] => {
        const session = (sessions ?? []).find((one) => (one as { id?: unknown })?.id === liveSessionId) as
            { users?: ApiLiveStudent[] } | undefined
        return (session?.users ?? EMPTY).filter((user) => user?.id != null && user.id !== meId)
    }
)

/** Ce que le centre lit du store, mémoïsé sur ses tranches. */
export const selectLiveCenterSlices = createSelector(
    [
        selectLiveSessionId,
        selectLiveStudents,
        selectCurrent,
        selectSeen,
        selectFocusRequest,
        selectOnline,
        selectCheckpoints,
    ],
    (
        liveSessionId,
        students,
        currentStudentActivity,
        seenStudentActivity,
        focusRequest,
        onlineIds,
        checkpointIds
    ): LiveCenterSlices => ({
        liveSessionId,
        students,
        currentStudentActivity,
        seenStudentActivity,
        focusRequest,
        onlineIds,
        checkpointIds,
    })
)

export function findLiveStudent(
    students: ApiLiveStudent[],
    studentId: string | null | undefined
): ApiLiveStudent | null {
    if (studentId == null) return null
    return students.find((student) => sameId(student.id, studentId)) ?? null
}

/** L'élève est dans la salle (`liveMode.liveParticipants`). */
export function isStudentOnline(slices: Pick<LiveCenterSlices, "onlineIds">, studentId: unknown): boolean {
    return slices.onlineIds.some((id) => sameId(id, studentId))
}

/** L'élève est en point d'avancement avec le champion (`liveMode.ongoingCheckpointIds`). */
export function isStudentWithHelper(slices: Pick<LiveCenterSlices, "checkpointIds">, studentId: unknown): boolean {
    return slices.checkpointIds.some((id) => sameId(id, studentId))
}

/** L'activité que l'élève a ouverte (`currentStudentActivity`), l'entrée du choix par défaut. */
export function currentActivityOf(slices: Pick<LiveCenterSlices, "currentStudentActivity">, studentId: unknown) {
    if (studentId == null) return null
    const value = slices.currentStudentActivity[String(studentId)]
    return value == null ? null : (value as string | number)
}

// ── L'activité présentée ───────────────────────────────────────────────────────────────────────────

/** Ce que montre une présentation : l'élève et son activité, que seule une autre activité ouverte change. */
export type PresentedActivity = { student: ApiLiveStudent; raw: ApiLiveActivity }

/**
 * L'activité que montre le centre pendant une présentation (son départ et son arrêt compris), quelle que
 * soit la sélection : les élèves voient la région du centre, toucher un autre élève ne doit jamais y
 * mettre sa vidéo ni son prénom (l'ancien `TodoModal` recouvrait la salle pendant la présentation, on ne
 * pouvait pas toucher un autre élève).
 *  - hors présentation : rien n'est figé (`null`) ;
 *  - la présentation part : l'activité ouverte à cet instant est figée, avec son élève ; partie sans
 *    activité (le partage de la barre au natif), la première ouverte ensuite l'est ;
 *  - tant qu'elle dure, **une autre activité ouverte la remplace**, avec son élève : la présentation suit
 *    l'activité que le centre montre (Wajih, live en prod du 03/11 : « un clic sur une autre activité ne fait
 *    rien »). Un élève touché sans activité ne change rien : ni sa vidéo ni son prénom n'y entrent ;
 *  - sinon l'activité présentée est relue dans les listes (un rendu qui arrive, une correction) ; sortie des
 *    listes (archivée ailleurs), la dernière lue reste — l'ancienne fiche gardait l'activité qu'elle montrait.
 * Rend `previous` tel quel quand rien n'a changé (une identité stable pour les mémos).
 */
export function presentedActivity(
    previous: PresentedActivity | null,
    input: {
        presenting: boolean
        /** L'élève sélectionné et l'activité que le centre lui choisit, hors présentation. */
        student: ApiLiveStudent | null
        raw: ApiLiveActivity | null
        students: ApiLiveStudent[]
    }
): PresentedActivity | null {
    if (!input.presenting) return null
    const opened = input.student != null && input.raw != null ? { student: input.student, raw: input.raw } : null
    if (previous == null) return opened
    if (opened != null && !sameActivity(opened, previous)) return opened
    const student = findLiveStudent(input.students, previous.student.id != null ? String(previous.student.id) : null)
    const raw = findLiveActivity(student, previous.raw.id)
    if (student == null || raw == null) return previous
    if (student === previous.student && raw === previous.raw) return previous
    return { student, raw }
}

function sameActivity(a: PresentedActivity, b: PresentedActivity): boolean {
    return sameId(a.student.id, b.student.id) && sameId(a.raw.id, b.raw.id)
}

// ── L'onglet ───────────────────────────────────────────────────────────────────────────────────────

export type TutorLiveCenterTabState = {
    studentId: string | null
    activityId: string | null
    hasQuiz: boolean
    mediaCount: number
    tab: UITutorLiveCenterTab
    split: boolean
}

export const initialTutorLiveCenterTab: TutorLiveCenterTabState = {
    studentId: null,
    activityId: null,
    hasQuiz: false,
    mediaCount: 0,
    tab: "visio",
    split: false,
}

export type TutorLiveCenterTabEvent =
    /** L'activité ouverte (ou son état : son rendu, son quiz) a changé. `none` : la visio ou la liste. */
    | {
          type: "activity"
          studentId: string | null
          activityId: string | null
          hasWork: boolean
          hasQuiz: boolean
          mediaCount: number
          none: UITutorLiveCenterTab
      }
    | { type: "choose"; tab: UITutorLiveCenterTab }
    | { type: "toggle_split" }
    | { type: "assessments"; studentId: string }

/**
 * L'onglet d'ouverture d'une activité (`TodoModal.getDefaultMode`) : le Rendu s'il y en a un, sinon le Quiz,
 * sinon l'Énoncé ; **toujours côte à côte** (l'énoncé et le rendu, quand l'onglet est l'un des deux et que le
 * centre a la place). L'ancien ne s'ouvrait côte à côte qu'avec un rendu ; Wajih, live en prod du 03/11 : « le
 * centre s'ouvre en côte à côte par défaut, c'est le mode le plus intuitif ».
 */
export function defaultCenterTab(hasWork: boolean, hasQuiz: boolean): Pick<TutorLiveCenterTabState, "tab" | "split"> {
    if (hasWork) return { tab: "work", split: true }
    if (hasQuiz) return { tab: "quiz", split: true }
    return { tab: "statement", split: true }
}

export function tutorLiveCenterTabReducer(
    state: TutorLiveCenterTabState,
    event: TutorLiveCenterTabEvent
): TutorLiveCenterTabState {
    switch (event.type) {
        case "activity": {
            const { studentId, activityId, hasWork, hasQuiz, mediaCount } = event
            if (activityId == null) {
                // « Contrôles » reste ouvert tant qu'on regarde le même élève ; il se ferme quand on change.
                const keepAssessments = state.tab === "assessments" && state.studentId === studentId
                const tab = keepAssessments ? "assessments" : event.none
                if (state.activityId == null && state.studentId === studentId && state.tab === tab) return state
                return { ...initialTutorLiveCenterTab, studentId, tab }
            }
            // Une autre activité : l'onglet revient au défaut (`TodoModal:1348-1358`).
            if (activityId !== state.activityId || studentId !== state.studentId) {
                return { studentId, activityId, hasQuiz, mediaCount, ...defaultCenterTab(hasWork, hasQuiz) }
            }
            // Une photo arrive : le rendu, côte à côte quand il a la place (`TodoModal:1380-1392`).
            if (mediaCount > state.mediaCount) {
                return { ...state, hasQuiz, mediaCount, tab: "work", split: true }
            }
            // « Retirer » le quiz : l'onglet est gardé par son nom, le quiz cède la place à l'énoncé.
            if (state.hasQuiz && !hasQuiz && state.tab === "quiz") {
                return { ...state, hasQuiz, mediaCount, tab: "statement" }
            }
            if (hasQuiz === state.hasQuiz && mediaCount === state.mediaCount) return state
            return { ...state, hasQuiz, mediaCount }
        }
        case "choose":
            return state.tab === event.tab ? state : { ...state, tab: event.tab }
        case "toggle_split":
            return { ...state, split: !state.split }
        case "assessments":
            return { ...initialTutorLiveCenterTab, studentId: event.studentId, tab: "assessments" }
    }
}

// ── Le quiz de prérequis ───────────────────────────────────────────────────────────────────────────

/**
 * Le quiz de l'activité, relu par le masteur (`PrerequisiteQuizDisplay`, source `activity`) : tenté, son
 * score (« {score}/{total} », le total compté sur les questions comme l'ancien) ; pas encore tenté, les
 * questions et leur bonne réponse, sans score (C-88). Sans questions : `null` (« Aucun quiz prérequis
 * disponible »).
 */
export function toUITutorQuizReview(raw: ApiLiveActivity | null | undefined): UIQuizReview | null {
    const questions = raw?.prerequisite_quiz?.questions
    if (!Array.isArray(questions) || questions.length === 0) return null
    const attempt = raw?.prerequisite_quiz_attempt ?? null
    const quiz: ApiQuizForReview = {
        questions,
        latest_attempt: attempt ?? { answers: [], score: 0, total: questions.length, passed: false },
    }
    const review = toQuizReview(quiz)
    if (review == null) return null
    const total = questions.length
    const score = attempt?.score || 0
    return {
        score,
        total,
        percentage: total > 0 ? Math.round((score / total) * 100) : 0,
        passed: attempt?.passed === true || attempt?.passed === 1,
        questions: review.questions.map(toUIQuizReviewQuestion),
        ...(attempt == null ? { attempted: false } : {}),
    }
}

/** L'id serveur de la question d'index donné (`quiz_question_id` du signalement). */
export function quizQuestionIdAt(raw: ApiLiveActivity | null | undefined, index: number): number | null {
    const question = raw?.prerequisite_quiz?.questions?.[index]
    return question?.id ?? null
}

/** « Quiz d'abord · Retirer » : un quiz à passer encore avant l'énoncé (`requiresPrerequisiteQuiz`). */
export function liveActivityRequiresQuiz(raw: ApiLiveActivity | null | undefined): boolean {
    if (raw == null) return false
    return requiresPrerequisiteQuiz(raw as Parameters<typeof requiresPrerequisiteQuiz>[0])
}

// ── L'activité regardée ────────────────────────────────────────────────────────────────────────────

export type TutorLiveFocusInput = {
    student: ApiLiveStudent | null
    activityId: string | null
    /** Le nom de la matière de l'activité (le catalogue), quand l'activité n'a pas de chapitre. */
    subjectName?: string | null
    /** Ce que l'élève a devant lui (`seenActivityOf`), et s'il est connecté. */
    seenActivityId: string | null
    online: boolean
    cleanUrl: (url: string | null | undefined) => string | null
    /** Ce que le crochet tient à côté : les avis, le visuel, l'aide. */
    correctionReview?: UICorrectionFeedback | null
    quizFeedback?: UICorrectionFeedback | null
    visual?: UITutorWhiteboardVisual
    statementAid?: UITutorStatementAid
}

/** `tutor_feedback` (1 / -1) en avis du livre. */
export function quizFeedbackOf(value: 1 | -1 | null): UICorrectionFeedback | null {
    return value === 1 ? "positive" : value === -1 ? "negative" : null
}

/**
 * L'activité regardée, en `UITutorLiveFocus`. `null` sans activité ouverte, ou si elle n'est plus dans
 * les listes de l'élève (le centre se ferme alors, C-08).
 */
export function tutorLiveCenterFocus(input: TutorLiveFocusInput): UITutorLiveFocus | null {
    const { student, activityId } = input
    const raw = findLiveActivity(student, activityId)
    if (student?.id == null || raw?.id == null) return null
    const work = toUITutorLiveWork(raw, input.cleanUrl)
    const quiz = raw.has_prerequisite_quiz ? toUITutorQuizReview(raw) : undefined
    return {
        studentId: String(student.id),
        activityId: String(raw.id),
        activityTitle: liveActivityTitle(raw),
        caption: liveActivityCaption(raw, input.subjectName),
        status: liveActivityStatus(raw),
        statement: toUITutorLiveStatement(raw.material),
        work,
        ...(quiz !== undefined ? { quiz } : {}),
        whiteboard: liveActivityHasWhiteboard(raw),
        shownToStudent: input.online && input.seenActivityId != null && input.seenActivityId === String(raw.id),
        ...(input.visual !== undefined ? { visual: input.visual } : {}),
        // L'avis sur la correction : demandé dès qu'elle a des points (C-74, `showStructuredCorrection`).
        ...(work?.correctionPoints != null && work.correctionPoints.length > 0
            ? { correctionReview: input.correctionReview ?? null }
            : {}),
        // L'avis sur le quiz : après la tentative seulement (`hasCompleted`).
        ...(raw.prerequisite_quiz_attempt != null && quiz != null ? { quizFeedback: input.quizFeedback ?? null } : {}),
        ...(input.statementAid !== undefined ? { statementAid: input.statementAid } : {}),
    }
}

/**
 * Les gestes offerts sur l'activité ouverte, dans l'ordre de la tête de `TodoModal` : « Afficher à
 * l'élève » (l'élève connecté), « Générer... » (`material_key_skills`, quand l'écran sait générer),
 * « Soumettre » (pas finie, un rendu), « Valider » (`rejected`), « Redonner » (finie), puis ⋮ :
 * « Modifier le titre » (pas finie) et « Archiver ». « Restaurer » n'existe qu'aux archives (C-41).
 */
export function tutorLiveCenterActions(
    raw: ApiLiveActivity | null | undefined,
    context: { online: boolean; canGenerate: boolean }
): UITutorActivityAction[] {
    if (raw?.id == null) return []
    const actions: UITutorActivityAction[] = []
    if (context.online) actions.push("show")
    if (context.canGenerate && raw.material?.material_key_skills) actions.push("generate")
    if (!raw.finished_at && liveActivityHasWork(raw)) actions.push("submit")
    if (raw.status === "rejected") actions.push("validate")
    if (raw.finished_at) actions.push("redo")
    if (!raw.finished_at) actions.push("editTitle")
    actions.push("archive")
    return actions
}

// ── La ligne de l'activité ─────────────────────────────────────────────────────────────────────────

/** Sous le titre de l'activité, au centre : son chapitre et son chrono (l'ancienne `ActivityItemInfo`). */
export type TutorLiveActivityInfo = {
    /** Le chapitre, à défaut le nom de la matière. */
    chapter?: string
    /** « 12 / 20 min », « - / 20 min » sans temps passé ; absent sans durée prévue. */
    chrono?: { label: string; tone?: UIActivityDurationTone }
}

/**
 * La ligne de l'activité (`ActivityItemInfo.js:14-20, 47-114`) : le chapitre (`student_chapter.chapter.name`,
 * à défaut la matière), le chrono quand une durée est prévue, son ton — orange (`over`) au-delà de 95 % du
 * temps prévu, vert en deçà, neutre sans temps passé (`calculateTimerColor(…, "finished")`,
 * `durationToneOf`). `undefined` sans activité ou sans rien à dire.
 */
export function liveActivityInfo(
    raw: ApiLiveActivity | null | undefined,
    subjectName: string | null | undefined
): TutorLiveActivityInfo | undefined {
    if (raw == null) return undefined
    const chapterName = raw.student_chapter?.chapter?.name
    const chapter = chapterName != null && chapterName !== "" ? chapterName : (subjectName ?? undefined)
    const planned = raw.estimated_duration
    const chrono =
        planned != null && planned !== 0
            ? {
                  label: `${raw.duration ? `${Math.floor(raw.duration / 60)} / ` : "- / "}${planned} min`,
                  tone: durationToneOf({ duration: raw.duration, estimated_duration: planned }),
              }
            : undefined
    if ((chapter == null || chapter === "") && chrono == null) return undefined
    return {
        ...(chapter != null && chapter !== "" ? { chapter } : {}),
        ...(chrono != null ? { chrono } : {}),
    }
}

// ── « Connexion faible » ───────────────────────────────────────────────────────────────────────────

const CONNECTION_TEXTS = {
    local: "Connexion faible. Déplace-toi.",
    one: (names: string) => `La connexion de ${names} est faible`,
    many: (names: string) => `Les connexions de ${names} sont faibles`,
}

/**
 * Les alertes « Connexion faible » du centre (`ConnectionQualityAlert.js:12-58`) : la mienne
 * (`liveMode.isLocalPoorConnection`), puis celle des élèves présents dont la qualité est mauvaise
 * (`poorConnectionQualityIds`, vue du tuteur), leurs noms d'affichage joints. Les deux tranches sont
 * écrites par l'écouteur global après 15 s de dégradation : elles partent quand le problème part (pas de
 * croix). Une alerte nomme un élève : le centre les tait quand l'onglet entier est partagé (`centerOnly`).
 */
export function tutorLiveConnectionAlerts(input: {
    localPoor: boolean
    poorIds: unknown[]
    slices: Pick<LiveCenterSlices, "students" | "onlineIds">
}): UITutorLiveAlert[] {
    const alerts: UITutorLiveAlert[] = []
    if (input.localPoor) alerts.push({ id: "connection-local", text: CONNECTION_TEXTS.local, tone: "warning" })
    const poor = input.slices.students.filter(
        (student) =>
            student.id != null &&
            input.slices.onlineIds.some((id) => sameId(id, student.id)) &&
            input.poorIds.some((id) => sameId(id, student.id))
    )
    if (poor.length > 0) {
        const names = poor
            .map((student) => student.display_name)
            .filter((name): name is string => name != null && name !== "")
            .join(", ")
        alerts.push({
            id: "connection-students",
            text: poor.length === 1 ? CONNECTION_TEXTS.one(names) : CONNECTION_TEXTS.many(names),
            tone: "warning",
        })
    }
    return alerts
}

// ── Ce que le public d'une présentation ne voit pas ────────────────────────────────────────────────

const AUDIENCE_TEXTS = {
    note: (count: number) =>
        count === 1
            ? "1 élève en Concentré ne voit pas la présentation"
            : `${count} élèves en Concentré ne voient pas la présentation`,
    allDialogue: "Tous en Dialogue",
}

/**
 * Pendant une présentation, la ligne du masteur : combien d'élèves présents sont en Concentré, et ne voient
 * donc pas le partage (chez l'élève, le Concentré masque la scène partagée : `StudentLivePage`, `sharing`).
 * Le mode et la présence sont ceux de la pellicule (`soundModeOf` sur `liveMode.soundModeIds`, toujours
 * Dialogue en séance individuelle ; `presenceOf` sur `liveMode.liveParticipants`). Jamais un prénom : au
 * centre seul, tout l'écran part chez les élèves. Rien hors présentation, rien quand personne n'est concerné.
 */
export function presentationAudienceNote(
    students: Pick<UITutorLiveStudent, "soundMode" | "presence">[],
    presenting: boolean
): { count: number; text: string; actionLabel: string } | null {
    if (!presenting) return null
    const count = students.filter((student) => student.presence === "here" && student.soundMode === "focused").length
    if (count === 0) return null
    return { count, text: AUDIENCE_TEXTS.note(count), actionLabel: AUDIENCE_TEXTS.allDialogue }
}
